<script setup lang="ts">
import { Button, Checkbox, Input, ZhPanel } from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { api } from '@/api'

const props = withDefaults(
    defineProps<{
        mcpUrl: string
        /** Where the server's permissions are read from. Left out, the route every connection form uses. */
        lookup?: (mcpUrl: string) => Promise<{ scopes: string[] }>
        /** What the choice is for, where it is not one connection being made. */
        intro?: string
        /** What to say while the server offers permissions and none is chosen. */
        emptyHint?: string
    }>(),
    {
        lookup: undefined,
        intro: 'Choose the access this connection needs. The server will ask you to authorize these permissions.',
        emptyHint: 'Select at least one permission before connecting.',
    },
)
const model = defineModel<string>({ required: true })
const available = ref<string[]>([])
const loading = ref(false)
const error = ref('')
const loaded = ref(false)
let revision = 0
const selected = computed(() => model.value.split(/\s+/).filter(Boolean))
/** Whether every permission in the list is chosen: the button above the list then clears them instead. */
const all = computed(() => available.value.every(scope => selected.value.includes(scope)))
/** Chooses every permission in the list, or none of them when all are chosen already. */
function toggleAll() {
    model.value = all.value ? '' : available.value.join(' ')
}
/** A permission typed in because the server's list does not name it. */
const other = ref('')
/** Adds what was typed to the choice and to the list, so it shows ticked like the rest. */
function addOther() {
    const typed = other.value.split(/\s+/).filter(Boolean)
    if (typed.length === 0) return
    available.value = [...new Set([...available.value, ...typed])]
    model.value = [...new Set([...selected.value, ...typed])].join(' ')
    other.value = ''
}
function permissionLabel(scope: string) {
    return scope
        .split(/[:._-]/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' · ')
}
function toggle(scope: string, checked: boolean | 'indeterminate') {
    model.value = (
        checked === true ? [...new Set([...selected.value, scope])] : selected.value.filter(s => s !== scope)
    ).join(' ')
}
/** The server address the ticked permissions were chosen for, once its permissions have loaded. */
let loadedFor: string | null = null
async function load() {
    const current = ++revision
    available.value = []
    loaded.value = false
    error.value = ''
    loading.value = false
    if (!/^https?:\/\//i.test(props.mcpUrl)) return
    loading.value = true
    try {
        const result = await (props.lookup ?? api.mcpOAuthPermissions)(props.mcpUrl)
        if (current !== revision) return
        if (loadedFor !== null && loadedFor !== props.mcpUrl) {
            // Another server: a permission ticked for the last one means nothing here, and a server refuses a
            // sign-in that asks for one it does not know, so only what this server offers stays ticked.
            model.value = selected.value.filter(scope => result.scopes.includes(scope)).join(' ')
        }
        available.value = [...new Set([...result.scopes, ...selected.value])]
        loaded.value = true
        loadedFor = props.mcpUrl
    } catch {
        if (current === revision) error.value = 'Could not load permissions. Check the server address and retry.'
    } finally {
        if (current === revision) loading.value = false
    }
}
watch(
    () => props.mcpUrl,
    () => {
        void load()
    },
    { immediate: true },
)
</script>

<template>
    <ZhPanel class="space-y-3" data-testid="mcp-permissions">
        <p class="text-sm font-medium">Permissions</p>
        <p class="text-muted-foreground text-sm">{{ intro }}</p>
        <p v-if="loading" role="status" class="text-sm">Loading permissions…</p>
        <p v-if="error" role="alert" class="text-sm">{{ error }}</p>
        <Button v-if="error || !loaded" type="button" variant="outline" size="sm" :disabled="loading || !mcpUrl" @click="load">Load permissions</Button>
        <Button v-if="available.length > 1" type="button" variant="outline" size="sm" data-testid="mcp-permissions-all" @click="toggleAll">
            {{ all ? 'Clear all' : `Select all ${available.length}` }}
        </Button>
        <label v-for="scope in available" :key="scope" class="flex items-center gap-2 text-sm">
            <Checkbox :model-value="selected.includes(scope)" @update:model-value="toggle(scope, $event)" />
            <span>{{ permissionLabel(scope) }} <code class="text-muted-foreground text-xs">{{ scope }}</code></span>
        </label>
        <p v-if="loaded && available.length && !selected.length" class="text-sm">{{ emptyHint }}</p>
        <p v-if="available.length && selected.length" class="text-muted-foreground text-sm" data-testid="mcp-permissions-count">
            {{ selected.length }} of {{ available.length }} chosen.
        </p>
        <!--
            A server's list is not always everything its sign-in accepts, so a permission it does not list can be
            typed in. It joins the list, ticked.
        -->
        <div v-if="available.length" class="flex items-end gap-2">
            <label class="block grow text-sm">
                <span>Add a permission the list does not show</span>
                <Input v-model="other" class="mt-1" placeholder="scope.one scope.two" data-testid="mcp-permissions-other" @keydown.enter.prevent="addOther" />
            </label>
            <Button type="button" variant="outline" size="sm" :disabled="!other.trim()" data-testid="mcp-permissions-add" @click="addOther">Add</Button>
        </div>
        <template v-if="!available.length">
            <p v-if="loaded" class="text-sm">This server does not advertise permissions. Connect to use its defaults, or enter permissions supplied by its administrator.</p>
            <label class="block text-sm"><span>Scopes (space-separated, optional)</span><Input v-model="model" class="mt-1" placeholder="scope.one scope.two" /></label>
        </template>
    </ZhPanel>
</template>
